import { wsLogger } from '@/shared/lib/logger';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys, cacheInvalidation } from '@/shared/lib/cache-invalidation';
import { payloadString } from '../realtime-sync.payload';
import type { DataChangedEvent } from '../realtime-sync.types';
import { getAppRouter } from '@/shared/lib/app-router';
import {
  useConceptStore,
  hasPendingSelection,
} from '@/features/design/concept/model/concept-editor-store';

export async function handleConceptAngleSelectionChanged(
  event: DataChangedEvent
): Promise<void> {
  const conceptAngleId = event.id;
  const selectedRenderId = payloadString(event.payload, 'selected_render_id');

  if (!conceptAngleId || !selectedRenderId) return;

  wsLogger.debug('[RealtimeSync] Render selection changed:', {
    conceptAngleId,
    selectedRenderId,
  });

  const store = useConceptStore.getState();

  // Skip while local debounce is pending — user's choice takes priority over server state
  if (hasPendingSelection(conceptAngleId)) return;

  // Skip if already matches (echo suppression)
  if (store.ui.selectedRenderId[conceptAngleId] === selectedRenderId) return;

  store.setSelectedRenderIdFromRemote(conceptAngleId, selectedRenderId);
}

/**
 * Handle concept created/updated — invalidate concept list and project detail
 * so the project page carousel and concept counts update in real-time.
 */
export async function handleConceptChanged(event: DataChangedEvent): Promise<void> {
  const payload = event.payload;
  const projectId = payloadString(payload, 'project_id');

  wsLogger.debug('[RealtimeSync] Concept changed:', {
    conceptId: event.id,
    action: event.action,
    projectId,
  });

  // Invalidate concept list for the project
  if (projectId) {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.concepts.list(projectId),
    });
    // Project detail may have updated concept count
    queryClient.invalidateQueries({
      queryKey: cacheKeys.projects.detail(projectId),
    });
  } else {
    // Fallback: invalidate all concept lists
    queryClient.invalidateQueries({ queryKey: cacheKeys.concepts.lists() });
  }
}

/**
 * Handle concept deleted — purge caches and redirect if the user is viewing the
 * deleted concept. Fires on both direct deletes and cascade deletes (e.g. main
 * angle deletion removes the parent concept on the server).
 */
export async function handleConceptDeleted(event: DataChangedEvent): Promise<void> {
  if (!event.id) return;

  const projectId = payloadString(event.payload, 'project_id');

  wsLogger.info('[RealtimeSync] Concept deleted:', {
    conceptId: event.id,
    projectId,
  });

  cacheInvalidation.afterDeleteConcept(event.id, projectId);

  // Redirect to project page if currently viewing the deleted concept.
  // Without this, the concept detail page keeps refetching and 404-looping.
  if (
    window.location.pathname.includes(`/concept/${event.id}`) &&
    projectId
  ) {
    getAppRouter()?.replace(`/project/${projectId}`);
  }
}
